import type { Archetype } from '@/lib/archetypes';
import type { DailyRecord, StreakState, ShotResult } from '@/lib/local-state';
import type { Outcome } from '@/lib/shot-resolution';
import type { Zone } from '@/lib/zones';
import type { GameMode } from '@/lib/game-mode';

export type PlayState = 'IDLE' | 'AIMING' | 'RESOLVING' | 'BETWEEN_SHOTS' | 'COMPLETE';

export type CalloutMode = 'preplay' | 'live' | 'truth';

export type SceneEnterPayload =
  | { scene: 'BOOT'; date: string; puzzleNumber: number }
  | { scene: 'MENU'; date: string; puzzleNumber: number; archetype: Archetype }
  | { scene: 'PLAY'; date: string; puzzleNumber: number; archetype: Archetype; record: DailyRecord }
  | { scene: 'RESULT'; date: string; puzzleNumber: number; archetype: Archetype; record: DailyRecord; streak: StreakState };

export type GameEvent =
  | { type: 'scene:enter'; payload: SceneEnterPayload }
  | { type: 'play:state'; payload: { state: PlayState; shotIndex: number } }
  | { type: 'play:shot-resolved'; payload: { index: number; shot: ShotResult; record: DailyRecord } }
  | { type: 'play:observations-update'; payload: { observations: Record<Zone, number> } }
  | { type: 'play:pulse-zone'; payload: { zone: Zone | null } }
  | { type: 'play:card-mode'; payload: { mode: CalloutMode } }
  | { type: 'play:mode'; payload: { mode: GameMode; hideHeatmap: boolean } }
  | { type: 'play:assist'; payload: { available: boolean; hint: Zone | null } };

// Re-export Outcome for convenience (subscribers map outcome → display color).
export type { Outcome };

type Subscriber = (event: GameEvent) => void;

const subs = new Set<Subscriber>();

export const gameBus = {
  subscribe(fn: Subscriber): () => void {
    subs.add(fn);
    return () => subs.delete(fn);
  },
  emit(event: GameEvent): void {
    for (const fn of subs) {
      try { fn(event); }
      catch (err) { console.error('[gameBus] subscriber threw', err); }
    }
  },
  clear(): void { subs.clear(); }, // test-only convenience
};
